import {
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  input,
  signal,
  viewChild,
  viewChildren
} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {SvgIconComponent} from '../svg-icon/svg-icon.component';
import {startWith, switchMap} from 'rxjs';

export interface SelectOption {
  label: string;
  value: any;
}

@Component({
  selector: 'app-custom-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SvgIconComponent],
  templateUrl: './custom-select.html',
  styleUrl: './custom-select.scss',
})
export class CustomSelect {
  label = input<string>('');
  options = input<SelectOption[] | string[]>([]);
  isLabelFixed = input<boolean>(false);
  control = input<FormControl>(new FormControl());
  selectId = input<string>('');
  required = input<boolean>(false);
  /** Testo mostrato al posto del valore finché non è scelta alcuna opzione. */
  placeholder = input<string>('');

  isOpen = signal(false);
  activeIndex = signal(-1);

  private readonly optionsList = viewChild<ElementRef<HTMLUListElement>>('optionsList');
  private readonly optionItems = viewChildren<ElementRef<HTMLLIElement>>('optionItem');

  protected readonly controlValue = toSignal(
    toObservable(this.control).pipe(
      switchMap(ctrl => ctrl.valueChanges.pipe(startWith(ctrl.value)))
    ),
    {initialValue: this.control().value}
  );

  protected readonly String = String;

  constructor() {
    effect(() => {
      const index = this.activeIndex();
      const list = this.optionsList()?.nativeElement;
      const items = this.optionItems();

      if (index >= 0 && list && items[index]) {
        const activeItem = items[index].nativeElement;
        const listRect = list.getBoundingClientRect();
        const itemRect = activeItem.getBoundingClientRect();

        if (itemRect.bottom > listRect.bottom) {
          list.scrollTop += (itemRect.bottom - listRect.bottom);
        }
        else if (itemRect.top < listRect.top) {
          list.scrollTop -= (listRect.top - itemRect.top);
        }
      }
    });
  }

  readonly normalizedOptions = computed<SelectOption[]>(() => {
    const opts = this.options();
    return opts.map(o => typeof o === 'string' ? {label: o, value: o} : o);
  });

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const container = target.closest('.select-container');
    if (this.isOpen() && !container) {
      this.isOpen.set(false);
    }
  }

  toggle() {
    if (this.isDisabled()) return;
    this.isOpen.update(v => !v);
    if (this.isOpen()) {
      this.updateActiveIndexFromValue();
    }
  }

  select(option: SelectOption) {
    if (this.isDisabled()) return;
    this.control().setValue(option.value);
    this.control().markAsTouched();
    this.isOpen.set(false);
  }

  onKeyDown(event: KeyboardEvent) {
    if (this.isDisabled()) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        if (!this.isOpen()) {
          this.isOpen.set(true);
          this.updateActiveIndexFromValue();
        }
        else if (this.activeIndex() >= 0) {
          this.select(this.normalizedOptions()[this.activeIndex()]);
        }
        event.preventDefault();
        break;
      case 'ArrowDown':
        if (!this.isOpen()) {
          this.isOpen.set(true);
        }
        this.activeIndex.update(i => (i + 1) % this.normalizedOptions().length);
        event.preventDefault();
        break;
      case 'ArrowUp':
        if (!this.isOpen()) {
          this.isOpen.set(true);
        }
        this.activeIndex.update(i => (i - 1 + this.normalizedOptions().length) % this.normalizedOptions().length);
        event.preventDefault();
        break;
      case 'Escape':
        this.isOpen.set(false);
        break;
      case 'Tab':
        this.isOpen.set(false);
        break;
    }
  }

  private updateActiveIndexFromValue() {
    const currentVal = this.controlValue();
    const index = this.normalizedOptions().findIndex(o => String(o.value) === String(currentVal));
    this.activeIndex.set(index);
  }

  readonly selectedLabel = computed(() => {
    const val = this.controlValue();
    const opt = this.normalizedOptions().find(o => String(o.value) === String(val));
    return opt ? opt.label : '';
  });

  // ID stabile: usa quello esterno, altrimenti ne genera uno casuale
  readonly uniqueId = computed(() =>
    this.selectId() || `custom-select-${Math.random().toString(36).substring(2, 9)}`
  );

  readonly errorId = computed(() => `${this.uniqueId()}-error`);

  readonly hasValue = computed(() => {
    const val = this.controlValue();
    return val !== null && val !== undefined && val !== '';
  });

  readonly isInvalid = computed(() => {
    this.controlValue(); // Trigger ricalcolo al cambio valore
    return this.control().invalid && this.control().touched;
  });

  readonly isDisabled = computed(() => this.control().disabled);
}
